import {
  AdminPractice,
  CustomAction,
  CustomerPracticeDevice,
  CustomVisitStatus,
  Segment,
  VisitTypeMembership,
} from 'ev-types';

import api, { Base, Tags } from 'ev-api/api';
import { sanitizeAndTransformResponse } from 'ev-api/common/transformers';
import { LanguageQuickSelect, VideoBackground } from 'ev-types/customer';

import { SharedViewsResponse } from '../customers/params';
import {
  CreateAdminCustomerVideoBackgroundParams,
  CreateCustomColumnParams,
  CreateCustomerLanguageQuickSelectParams,
  CreateCustomStatusParams,
  CustomerIdParam,
  DeleteAdminCustomerVideoBackgroundParams,
  DeleteCustomColumnParams,
  DeleteCustomerLanguageQuickSelectParams,
  DeleteCustomStatusParams,
  GetColumnsParams,
  GetCustomActionsParams,
  GetCustomDataKeyMapParams,
  GetCustomStatusesParams,
  GetShortenedUrlsForPracticeParams,
  GetVisitTypeMembershipsForPracticeAndVisitTypeParams,
  UpdateAdminCustomerVideoBackgroundParams,
  UpdateAllCustomerPracticeParams,
  UpdateCustomActionParams,
  UpdateCustomColumnOrderParams,
  UpdateCustomColumnParams,
  UpdateCustomerLanguageQuickSelectParams,
  UpdateCustomerParams,
  UpdateCustomerPracticeParams,
  UpdateCustomStatusOrderParams,
  UpdateCustomStatusParams,
  UpdateSegmentParams,
} from './params';
import { GetShortenedUrlsForPracticeResponse } from './responses';
import { ColumnData, CustomDataKeyMap, Customer } from './types';

const adminApi = api.injectEndpoints({
  endpoints: builder => ({
    getColumns: builder.query<ColumnData[], GetColumnsParams>({
      query: ({ customerId, columnSection, eDataDefinitionName }) => ({
        url: `${Base.V4}/admin/get_columns`,
        params: {
          customer_id: customerId,
          column_section: columnSection,
          edata_definition_name: eDataDefinitionName,
        },
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.CustomColumns],
    }),
    getCustomDataKeyMap: builder.query<
      CustomDataKeyMap,
      GetCustomDataKeyMapParams
    >({
      query: ({ customerId, columnSection }) => ({
        url: `${Base.V4}/admin/custom_data_key_map`,
        params: {
          customer_id: customerId,
          column_section: columnSection,
        },
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.CustomColumns],
    }),
    getSegments: builder.query<Segment[], CustomerIdParam>({
      query: ({ customerId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/segments`,
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.Segments],
    }),
    getCustomStatuses: builder.query<
      CustomVisitStatus[],
      GetCustomStatusesParams
    >({
      query: ({ segmentId }) => ({
        url: `${Base.V4}/admin/custom_statuses`,
        params: {
          segment_id: segmentId,
        },
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.VisitStatuses],
    }),
    updateSegment: builder.mutation<void, UpdateSegmentParams>({
      query: ({ customerId, order, name, segmentId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/segments/${segmentId}`,
        method: 'PUT',
        body: {
          order,
          name,
        },
      }),
      invalidatesTags: (_result, _error, args) =>
        args.skipTagsInvalidation ? [] : [Tags.Segments],
    }),
    getCustomer: builder.query<Customer, CustomerIdParam>({
      query: ({ customerId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}`,
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.Customer],
    }),
    updateCustomer: builder.mutation<void, UpdateCustomerParams>({
      query: data => ({
        method: 'PATCH',
        url: `${Base.V4}/admin/customers/${data.customerId}`,
        body: data,
      }),
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.Customer, Tags.AdminLanguageQuickSelects],
    }),
    getCustomerPracticeDevices: builder.query<
      CustomerPracticeDevice[],
      CustomerIdParam
    >({
      query: ({ customerId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/practice_devices`,
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.PracticeDevices],
    }),
    getCustomerPractices: builder.query<AdminPractice[], CustomerIdParam>({
      query: ({ customerId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/practices`,
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.AdminPractices],
    }),
    updateCustomerPractice: builder.mutation<
      void,
      UpdateCustomerPracticeParams
    >({
      query: ({
        customerId,
        practiceId,
        supportsThirdPartyInterpreters,
        supportsTelephonyServices,
        supportsAudioToVideo,
        supportsInboundTelephony,
        supports_skipping_welcome_page_on_connected_devices,
      }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/practices/${practiceId}`,
        method: 'PATCH',
        body: {
          supports_third_party_interpreters: supportsThirdPartyInterpreters,
          supports_telephony_services: supportsTelephonyServices,
          supports_audio_to_video: supportsAudioToVideo,
          supports_inbound_telephony: supportsInboundTelephony,
          supports_skipping_welcome_page_on_connected_devices,
        },
      }),
      invalidatesTags: [Tags.AdminPractices],
    }),
    updateAllCustomerPractice: builder.mutation<
      void,
      UpdateAllCustomerPracticeParams
    >({
      query: ({
        customerId,
        supportsThirdPartyInterpreters,
        supportsTelephonyServices,
        supportsAudioToVideo,
        supportsInboundTelephony,
      }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/practices/update_all`,
        method: 'PATCH',
        body: {
          supports_third_party_interpreters: supportsThirdPartyInterpreters,
          supports_telephony_services: supportsTelephonyServices,
          supports_audio_to_video: supportsAudioToVideo,
          supports_inbound_telephony: supportsInboundTelephony,
        },
      }),
      invalidatesTags: [Tags.AdminPractices],
    }),
    createCustomColumn: builder.mutation<void, CreateCustomColumnParams>({
      query: ({
        customerId,
        columnLabelId,
        columnLabel,
        entityType,
        columnSection,
        visible,
        available,
      }) => ({
        url: `${Base.V4}/admin/create_custom_column`,
        method: 'POST',
        body: {
          customer_id: customerId,
          column_label_id: columnLabelId,
          column_label: columnLabel,
          entity_type: entityType,
          column_section: columnSection,
          visible,
          available,
        },
      }),
      invalidatesTags: [Tags.CustomColumns],
    }),
    updateCustomColumnOrder: builder.mutation<
      void,
      UpdateCustomColumnOrderParams
    >({
      query: ({ customerId, section, orderedColumnsIds }) => ({
        url: `${Base.V4}/admin/custom_columns/update_order`,
        method: 'PUT',
        body: {
          customer_id: customerId,
          section,
          ordered_columns_ids: orderedColumnsIds,
        },
      }),
      invalidatesTags: [Tags.CustomColumns],
    }),
    updateCustomColumn: builder.mutation<void, UpdateCustomColumnParams>({
      query: ({
        customerId,
        customColumnId,
        columnLabelId,
        columnLabel,
        visible,
        available,
        order,
        display_format,
        columnSections,
        removeExistingSections,
      }) => ({
        url: `${Base.V4}/admin/update_custom_column`,
        method: 'PUT',
        body: {
          customer_id: customerId,
          custom_column_id: customColumnId,
          column_label_id: columnLabelId,
          column_label: columnLabel,
          visible,
          available,
          order,
          display_format,
          column_sections: columnSections,
          remove_existing_sections: removeExistingSections,
        },
      }),
      invalidatesTags: (_result, _error, args) =>
        args.skipTagsInvalidation ? [] : [Tags.CustomColumns],
    }),
    deleteCustomColumn: builder.mutation<void, DeleteCustomColumnParams>({
      query: ({ customColumnId }) => ({
        url: `${Base.V4}/admin/delete_custom_column`,
        params: { custom_column_id: customColumnId },
        method: 'DELETE',
      }),
      invalidatesTags: [Tags.CustomColumns],
    }),
    createCustomStatus: builder.mutation<void, CreateCustomStatusParams>({
      query: ({ segmentId, order, name, color }) => ({
        url: `${Base.V4}/admin/custom_statuses`,
        method: 'POST',
        body: {
          segment_id: segmentId,
          name,
          order,
          color,
        },
      }),
      invalidatesTags: [Tags.VisitStatuses],
    }),
    updateCustomStatus: builder.mutation<void, UpdateCustomStatusParams>({
      query: ({ segmentId, statusId, order, name, color }) => ({
        url: `${Base.V4}/admin/custom_statuses/${statusId}`,
        method: 'PUT',
        body: {
          segment_id: segmentId,
          name,
          order,
          color,
        },
      }),
      invalidatesTags: [Tags.VisitStatuses],
    }),
    updateCustomStatusOrder: builder.mutation<
      void,
      UpdateCustomStatusOrderParams
    >({
      query: ({ segmentId, order }) => ({
        url: `${Base.V4}/admin/custom_statuses/reorder`,
        method: 'PUT',
        body: {
          segment_id: segmentId,
          order,
        },
      }),
      invalidatesTags: [Tags.VisitStatuses],
    }),
    deleteCustomStatus: builder.mutation<void, DeleteCustomStatusParams>({
      query: ({ segmentId, statusId, newStatusId }) => ({
        url: `${Base.V4}/admin/custom_statuses/${statusId}`,
        params: { segment_id: segmentId, new_status_id: newStatusId },
        method: 'DELETE',
      }),
      invalidatesTags: [Tags.VisitStatuses],
    }),
    getCustomActions: builder.query<CustomAction[], GetCustomActionsParams>({
      query: ({ customerId, section }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/custom_actions`,
        params: {
          section,
        },
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.CustomActions],
    }),
    updateCustomAction: builder.mutation<void, UpdateCustomActionParams>({
      query: ({ section, customerId, labelId, label, visible, action }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/custom_actions`,
        method: 'POST',
        body: {
          label,
          visible,
          action,
          section,
          label_id: labelId,
        },
      }),
      invalidatesTags: [Tags.CustomActions],
    }),
    getCustomerLanguageQuickSelects: builder.query<
      LanguageQuickSelect[],
      CustomerIdParam
    >({
      query: ({ customerId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/language_quick_selects`,
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.AdminLanguageQuickSelects],
    }),
    createCustomerLanguageQuickSelect: builder.mutation<
      void,
      CreateCustomerLanguageQuickSelectParams
    >({
      query: ({ customerId, language, position }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/language_quick_selects`,
        method: 'POST',
        body: {
          language,
          position,
        },
      }),
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.AdminLanguageQuickSelects],
    }),
    updateCustomerLanguageQuickSelect: builder.mutation<
      void,
      UpdateCustomerLanguageQuickSelectParams
    >({
      query: ({ customerId, languageQuickSelectId, audioTarget }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/language_quick_selects/${languageQuickSelectId}`,
        method: 'PATCH',
        body: {
          audio_target: audioTarget,
        },
      }),
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.AdminLanguageQuickSelects],
    }),
    deleteCustomerLanguageQuickSelect: builder.mutation<
      void,
      DeleteCustomerLanguageQuickSelectParams
    >({
      query: ({ customerId, languageQuickSelectId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/language_quick_selects/${languageQuickSelectId}`,
        method: 'DELETE',
      }),
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.AdminLanguageQuickSelects],
    }),
    getAdminCustomerVideoBackgrounds: builder.query<
      VideoBackground[],
      CustomerIdParam
    >({
      query: ({ customerId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/video_backgrounds`,
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.AdminVideoBackgrounds],
    }),
    createAdminCustomerVideoBackground: builder.mutation<
      void,
      CreateAdminCustomerVideoBackgroundParams
    >({
      query: ({ customerId, kind, background }) => {
        const formData = new FormData();
        formData.append('kind', kind);
        formData.append('background', background);
        return {
          url: `${Base.V4}/admin/customers/${customerId}/video_backgrounds`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.AdminVideoBackgrounds],
    }),
    updateAdminCustomerVideoBackground: builder.mutation<
      void,
      UpdateAdminCustomerVideoBackgroundParams
    >({
      query: ({ customerId, videoBackgroundId, kind, background }) => {
        const formData = new FormData();
        formData.append('kind', kind);
        formData.append('background', background);
        return {
          url: `${Base.V4}/admin/customers/${customerId}/video_backgrounds/${videoBackgroundId}`,
          method: 'PATCH',
          body: formData,
        };
      },
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.AdminVideoBackgrounds],
    }),
    deleteAdminCustomerVideoBackground: builder.mutation<
      void,
      DeleteAdminCustomerVideoBackgroundParams
    >({
      query: ({ customerId, videoBackgroundId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/video_backgrounds/${videoBackgroundId}`,
        method: 'DELETE',
      }),
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.AdminVideoBackgrounds],
    }),
    getShortenedUrlsForPractice: builder.query<
      GetShortenedUrlsForPracticeResponse,
      GetShortenedUrlsForPracticeParams
    >({
      query: ({ preSlug, slug, practiceHandle }) => ({
        url: `${Base.V3}/shortened_url/${preSlug}/${slug}/${practiceHandle}/links`,
      }),
    }),
    getVisitTypeMembershipsForPracticeAndVisitType: builder.query<
      VisitTypeMembership[],
      GetVisitTypeMembershipsForPracticeAndVisitTypeParams
    >({
      query: ({ practiceId, visitTypeId }) => ({
        url: `${Base.V3}/practices/${practiceId}/visit_types/${visitTypeId}/visit_type_memberships`,
      }),
      transformResponse: sanitizeAndTransformResponse,
    }),
    getSharedViews: builder.query<SharedViewsResponse, CustomerIdParam>({
      query: ({ customerId }) => ({
        url: `${Base.V4}/admin/customers/${customerId}/shared_filter_views`,
      }),
      transformResponse: sanitizeAndTransformResponse,
    }),
  }),
});

export const {
  useGetColumnsQuery,
  useUpdateCustomColumnMutation,
  useUpdateCustomColumnOrderMutation,
  useUpdateSegmentMutation,
  useCreateCustomColumnMutation,
  useDeleteCustomColumnMutation,
  useCreateCustomStatusMutation,
  useUpdateCustomStatusMutation,
  useUpdateCustomStatusOrderMutation,
  useDeleteCustomStatusMutation,
  useGetCustomerQuery,
  useUpdateCustomerMutation,
  useGetCustomerPracticeDevicesQuery,
  useGetCustomerPracticesQuery,
  useUpdateCustomerPracticeMutation,
  useUpdateAllCustomerPracticeMutation,
  useGetSegmentsQuery,
  useGetCustomDataKeyMapQuery,
  useGetCustomStatusesQuery,
  useGetCustomActionsQuery,
  useUpdateCustomActionMutation,
  useGetCustomerLanguageQuickSelectsQuery,
  useCreateCustomerLanguageQuickSelectMutation,
  useUpdateCustomerLanguageQuickSelectMutation,
  useDeleteCustomerLanguageQuickSelectMutation,
  useGetAdminCustomerVideoBackgroundsQuery,
  useCreateAdminCustomerVideoBackgroundMutation,
  useDeleteAdminCustomerVideoBackgroundMutation,
  useGetShortenedUrlsForPracticeQuery,
  useGetVisitTypeMembershipsForPracticeAndVisitTypeQuery,
  useGetSharedViewsQuery,
} = adminApi;
